import React, { Fragment, useMemo } from 'react';
import cn from 'classnames';
import { Tab } from '@headlessui/react';
import { SubTabsProps } from 'apps/sigma2-ui/src/lib/hooks/useSubTabGroup';
import { SubTabsTabDefinition } from 'apps/sigma2-ui/src/lib/hooks/useSubTabGroup';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const SubTabsHeader = ({
  isSelected,
  translatedContent,
}: Parameters<SubTabsTabDefinition['header']>[0] & {
  translatedContent: TranslatedContent;
}): React.ReactElement => {
  return (
    <S2Typography
      variant={'headline-6-alt'}
      translatedContent={translatedContent}
      textColorVariant={isSelected ? 'royal-600' : 'gray-600'}
    />
  );
};

export const SubTabsCountWithHeader = ({
  isSelected,
  count,
}: Parameters<SubTabsTabDefinition['header']>[0] & {
  count: number;
}): React.ReactElement => {
  const language = useLanguage();

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full p-1 h-[24px] w-[24px]',
        {
          'bg-royal-200': isSelected,
          'bg-gray-200': !isSelected,
        },
      )}
    >
      <S2Typography
        variant={'small-label-alt'}
        translatedContent={language.dangerousUserInput({
          value: `${count}`,
        })}
        textColorVariant={isSelected ? 'royal-600' : 'gray-600'}
      />
    </div>
  );
};

const shadows = {
  left: 'shadow-[2px_-2px_4px_0px_#00000014_inset]',
  right: 'shadow-[-2px_-2px_4px_0px_#00000014_inset]',
};

/**
 * Sub Tab component for showing sibling content within a larger card, as in the data intelligence deep dive page
 * It uses @headlessui/react for improved accessibility and keyboard navigation
 * @see useSubTabGroup for state handling logic
 * @url https://www.figma.com/design/70fJfZJAnTZfJkslSuUdPS/Sigma-Squared-(Dev-Ready)?node-id=4205-20300&t=87Tcmo87hob2hn7r-0
 * @note this component ensures all tabs are the same width and any text that doesn't fit is truncated
 */
export const SubTabs: React.FC<SubTabsProps> = ({
  tabDefinitions,
  selectedTabIndex,
  onTabChange,
}) => {
  const numTabs = useMemo(() => tabDefinitions.length, [tabDefinitions]);

  return (
    <Card paddingClassName="p-0 h-full">
      <Tab.Group selectedIndex={selectedTabIndex}>
        <Tab.List className="flex justify-between w-full">
          {tabDefinitions.map(({ header }, index) => {
            const isFirstTab = index === 0;
            const isLastTab = index === numTabs - 1;
            const isLeftOfSelected = index < selectedTabIndex;
            const isRightOfSelected = index > selectedTabIndex;

            return (
              <Tab key={index} as={Fragment}>
                {({ selected: isSelected }) => {
                  return (
                    <div
                      className={cn(
                        'w-full hover:bg-gray-100 hover:border-t-gray-100 transition-colors duration-200 truncate overflow-hidden whitespace-nowrap',
                        {
                          'rounded-tl-md': isFirstTab,
                          'rounded-tr-md': isLastTab,
                          [shadows.left]: isRightOfSelected,
                          [shadows.right]: isLeftOfSelected,
                        },
                      )}
                    >
                      <button
                        onClick={() => onTabChange(index)}
                        className={cn(
                          'flex flex-1 items-center text-center flex-nowrap py-2 w-full border-t-2',
                          {
                            'border-t-royal-600': isSelected,
                            'border-t-transparent': !isSelected,
                            'rounded-tl-md': isFirstTab,
                            'rounded-tr-md': isLastTab,
                          },
                        )}
                      >
                        <span className="py-2 px-4 text-center w-full">
                          {header({ isSelected })}
                        </span>
                      </button>
                    </div>
                  );
                }}
              </Tab>
            );
          })}
        </Tab.List>

        <Tab.Panels>
          {tabDefinitions.map(({ body }, index) => {
            return (
              <Tab.Panel key={index} className={'px-8 py-10'}>
                {body()}
              </Tab.Panel>
            );
          })}
        </Tab.Panels>
      </Tab.Group>
    </Card>
  );
};
